import { InputForm } from '@/components/new/form/InputForm'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import { useModalWizardStepsContext } from '@/components/new/modalWizardSteps/ModalWizardStepsContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProviders } from '@/api/providersAPI'
import { IContainer } from '../../../interfaces/interfaces'
import { useEffect } from 'react'
import { SelectFormProviders } from '../../../components/SelectFormProviders'
import { Label } from '@/components/ui/label'
import useContainerWizzardSteps, {
  Selector,
} from '@/state/useContainerWizzardSteps'
import SpinnerLoading from '@/components/misc/SpinnerLoading'
import Select from '@/components/forms/Select'
import { CheckCircleIcon, CircleXIcon } from 'lucide-react'
import { useContainerDetails } from '../../../hooks/useContainerDetails'
import APIServices from '@/api/APIServices'
import { toast } from 'react-toastify'
import OriginDestinyManager from '../../../components/OriginDestinyManager'
import { useState } from 'react'
import { ToggleForm } from '@/components/new/form/ToggleForm'
import { SelectFormOriginDestiny } from '../../../components/SelectFormOriginDestiny'

export default function DetailsAdvancedContainerTab({
  isEditing,
  editContainer,
}: {
  isEditing?: boolean
  editContainer?: IContainer
}) {
  const context = isEditing
    ? useModalWizardTabsContext()
    : useModalWizardStepsContext()
  const { useFormWizzard } = context
  const { control, setValue, getValues, watch } = useFormWizzard

  const data = useContainerWizzardSteps((state) => state.data)
  const updateData = useContainerWizzardSteps((state) => state.updateData)
  const queryClient = useQueryClient()

  // Use shared container details hook
  const { containerDetails, isLoading } = useContainerDetails(editContainer?.id)

  // Queries for async data
  const { data: providers } = useQuery({
    queryKey: ['providers'],
    queryFn: () =>
      getProviders({
        page: 1,
        per_page: 10,
        search: '',
      }),
    staleTime: 5 * 60 * 1000,
  })

  // Async fetchers for AsyncSelectForm
  const fetchProviders = async (search: string): Promise<{ id: string; label: string }[]> => {
    const response = await getProviders({ page: 1, per_page: 10, search });
    if (!Array.isArray(response)) return [];
    return response.map(({ id, name }) => ({
      id: String(id),
      label: name,
    }));
  };

  // Set form values when container details are loaded
  useEffect(() => {
    if (containerDetails && isEditing) {
      console.log('DetailsAdvanced - Container details:', containerDetails); // Debug log
      
      // Para AsyncSelectForm, el valor debe coincidir con el id del option
      if (containerDetails.origin && containerDetails.origin.id) {
        console.log('Setting origin:', containerDetails.origin.id); // Debug log
        setValue('origin', containerDetails.origin.id)
        setSelectedOrigin(containerDetails.origin) // También setear el estado local
      }
      
      if (containerDetails.destiny && containerDetails.destiny.id) {
        console.log('Setting destiny:', containerDetails.destiny.id); // Debug log
        setValue('destiny', containerDetails.destiny.id)
        setSelectedDestiny(containerDetails.destiny) // También setear el estado local
      }
      
      setValue('netWeight', containerDetails.netWeight)
      setValue('grossWeight', containerDetails.grossWeight)
      setValue('code', containerDetails.code)
      setValue('sealNumber', containerDetails.sealNumber)
      setValue('isRefrigered', containerDetails.isRefrigered)
      setValue('temperatureRange', containerDetails.temperatureRange)
      setValue('trackingNumber', containerDetails.trackingNumber)
    }
  }, [containerDetails, isEditing, setValue])

  // Mutation for updating container advanced details
  const updateContainerAdvancedDetails = useMutation({
    mutationFn: async (detailsData: {
      originId?: number
      destinyId?: number
      netWeight?: number
      grossWeight?: number
      code?: string
      sealNumber?: string
      isRefrigered?: boolean
      temperatureRange?: string
      trackingNumber?: string
    }) => {
      if (!editContainer?.id) throw new Error('Container ID is required')
      const response = await APIServices.patch(`/cargo/containers/${editContainer.id}/details`, detailsData)
      return response.data
    },
    onSuccess: () => {
      toast.success('Información de detalles avanzados actualizada correctamente')
      queryClient.invalidateQueries({ queryKey: ['container_details', editContainer?.id] })
    },
    onError: (error: any) => {
      console.error('Error updating container advanced details:', error)
      toast.error(error?.response?.data?.message || 'Error al actualizar la información de detalles avanzados')
    },
  })

  // Function to handle saving advanced details data
  const handleSaveAdvancedDetails = async () => {
    const formValues = getValues()
    
    const detailsData: {
      originId?: number
      destinyId?: number
      netWeight?: number
      grossWeight?: number
      code?: string
      sealNumber?: string
      isRefrigered?: boolean
      temperatureRange?: string
      trackingNumber?: string
    } = {}

    // Only include fields that have values
    if (formValues.origin) {
      detailsData.originId = typeof formValues.origin === 'string' 
        ? Number(formValues.origin) 
        : Number(formValues.origin.id)
    }
    if (formValues.destiny) {
      detailsData.destinyId = typeof formValues.destiny === 'string' 
        ? Number(formValues.destiny) 
        : Number(formValues.destiny.id)
    }
    if (formValues.netWeight) {
      detailsData.netWeight = Number(formValues.netWeight)
    }
    if (formValues.grossWeight) {
      detailsData.grossWeight = Number(formValues.grossWeight)
    }
    if (formValues.code) {
      detailsData.code = formValues.code
    }
    if (formValues.sealNumber) {
      detailsData.sealNumber = formValues.sealNumber
    }
    if (formValues.isRefrigered === true) {
      detailsData.isRefrigered = true
    }
    if (formValues.temperatureRange) {
      detailsData.temperatureRange = formValues.temperatureRange
    }
    if (formValues.trackingNumber) {
      detailsData.trackingNumber = formValues.trackingNumber
    }

    await updateContainerAdvancedDetails.mutateAsync(detailsData)
  }

  // Expose the save function to the parent component
  useEffect(() => {
    if (isEditing) {
      // @ts-ignore - Adding custom property to window for parent access
      window.handleSaveAdvancedDetails = handleSaveAdvancedDetails
      // @ts-ignore - Expose loading state
      window.isSavingAdvancedDetails = updateContainerAdvancedDetails.isPending
    }
    
    return () => {
      // @ts-ignore
      delete window.handleSaveAdvancedDetails
      // @ts-ignore
      delete window.isSavingAdvancedDetails
    }
  }, [isEditing, handleSaveAdvancedDetails, updateContainerAdvancedDetails.isPending])

  // Watch form changes and update data - using a single useEffect to avoid infinite loops
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name) {
        updateData({ ...data, [name]: value[name] })
      }
    })
    
    return () => subscription.unsubscribe()
  }, [watch, updateData])

  // Estado para el valor seleccionado de origen y destino (para el modal)
  const [selectedOrigin, setSelectedOrigin] = useState<{ id: number; name: string } | undefined>(undefined)
  const [selectedDestiny, setSelectedDestiny] = useState<{ id: number; name: string } | undefined>(undefined)

  return (
    <div className="h-[60dvh] overflow-y-auto">
      <div className={'space-y-5 p-4'} style={isEditing ? { marginTop: '-.5rem' } : {}}>
        {/* Origen */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <SelectFormOriginDestiny
              name="origin"
              control={control}
              label="Origen"
              placeholder="Buscar origen..."
              notFoundMessage="No se encontraron orígenes."
            />
          </div>
          <div className="shrink-0 ml-2">
            <OriginDestinyManager
              type="origin"
              onSelect={(item) => {
                setValue('origin', { id: item.id, name: item.name })
                setSelectedOrigin(item)
              }}
              selectedValue={selectedOrigin}
            />
          </div>
        </div>

        {/* Destino */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <SelectFormOriginDestiny
              name="destiny"
              control={control}
              label="Destino"
              placeholder="Buscar destino..."
              notFoundMessage="No se encontraron destinos."
            />
          </div>
          <div className="shrink-0 ml-2">
            <OriginDestinyManager
              type="destiny"
              onSelect={(item) => {
                setValue('destiny', { id: item.id, name: item.name })
                setSelectedDestiny(item)
              }}
              selectedValue={selectedDestiny}
            />
          </div>
        </div>

        {/* Peso neto */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <InputForm
              name="netWeight"
              label="Peso neto"
              placeholder="Peso neto"
              control={control}
              type="number"
              defaultValue={containerDetails?.netWeight}
            />
          </div>
        </div>

        {/* Peso bruto */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <InputForm
              name="grossWeight"
              label="Peso bruto"
              placeholder="Peso bruto"
              control={control}
              type="number"
              defaultValue={containerDetails?.grossWeight}
            />
          </div>
        </div>

        {/* Código */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <InputForm
              name="code"
              label="Código"
              placeholder="Código"
              control={control}
              defaultValue={containerDetails?.code}
            />
          </div>
        </div>

        {/* Sello */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <InputForm
              name="sealNumber"
              label="Sello"
              placeholder="Número de sello"
              control={control}
              defaultValue={containerDetails?.sealNumber}
            />
          </div>
        </div>

        {/* Es refrigerado */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <ToggleForm
              name="isRefrigered"
              control={control}
              label="Es refrigerado"
              defaultValue={containerDetails?.isRefrigered}
            />
          </div>
        </div>

        {/* Rango de temperatura */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <InputForm
              name="temperatureRange"
              label="Rango de temperatura"
              placeholder="Rango de temperatura"
              control={control}
              defaultValue={containerDetails?.temperatureRange}
            />
          </div>
        </div>

        {/* Tracking */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <InputForm
              name="trackingNumber"
              label="Tracking"
              placeholder="Número de tracking"
              control={control}
              defaultValue={containerDetails?.trackingNumber}
            />
          </div>
        </div>
      </div>
    </div>
  )
}